/**
 * Shapes the tax page figures into the Property Portfolio layout
 * (Fan, 2026-10-27: "It should look more like the Property Portfolio scope").
 *
 * Pure display arithmetic over figures the backend already computed:
 *   - reserves:  GET /tax-reserves/group-summary
 *   - debts:     GET /payroll/overview (obligations)
 *   - paid:      GET /reports/tax-paid (lines + refunds + fx_rates_used)
 *   - VAT returns: GET /payroll/vat-returns
 * Nothing here is posted anywhere. Cross-currency totals are converted with
 * the report's own fx_rates_used and labelled "approx" in the UI; an amount
 * whose currency has no rate is left out of the converted total and counted
 * in `unconverted` so the page can say so (never treated as 1.0).
 */

import { mergeTaxItems, buildPaymentPlans } from './taxReturns';

export const TAX_KINDS = [
  { key: 'vat', label: 'VAT', color: '#3B82F6' },
  { key: 'corporation_tax', label: 'Corporation tax', color: '#8B5CF6' },
  { key: 'paye', label: 'PAYE and NI', color: '#F59E0B' },
  { key: 'vat_paye', label: 'VAT and PAYE arrears', color: '#6366F1' },
  { key: 'income_tax', label: 'Income tax', color: '#10B981' },
  { key: 'property_tax', label: 'Property tax', color: '#14B8A6' },
  { key: 'seizure', label: 'Seized by the tax office', color: '#DC2626' },
  { key: 'other', label: 'Other tax', color: '#9CA3AF' },
];
const KIND_KEYS = new Set(TAX_KINDS.map((k) => k.key));

export const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const OPEN_STATES_EXCLUDED = new Set(['paid', 'cancelled']);

export function kindKey(kind) {
  if (!kind) return 'other';
  if (kind === 'payroll_paye' || kind === 'paye' || kind === 'payroll_tax') return 'paye';
  return KIND_KEYS.has(kind) ? kind : 'other';
}

/**
 * Tax type of a debt: tax_obligations.tax_type when set, else payroll kind,
 * else the label (same rule as the debts panel, PR #246).
 */
export function debtKind(o) {
  if (!o) return 'other';
  if (o.kind === 'payroll_paye') return 'paye';
  const t = `${o.tax_type || ''} ${o.tax_type ? '' : o.label || ''}`.toLowerCase();
  const vat = /\bvat\b|\btva\b|umsatz|finanzamt/.test(t);
  const paye = /paye|\bni\b/.test(t);
  if (vat && paye) return 'vat_paye';
  if (vat) return 'vat';
  if (/corporation|\bis\b|imp[oô]t sur les soci/.test(t)) return 'corporation_tax';
  if (paye) return 'paye';
  if (/property|fonci|\bcfe\b/.test(t)) return 'property_tax';
  if (/income/.test(t)) return 'income_tax';
  return 'other';
}

function blankMonths() {
  return MONTH_LABELS.map(() => ({ reserved: 0, paid: 0, refunds: 0, due: 0 }));
}

/** Converter from any currency to GBP using the report's rates (null = no rate). */
export function makeToGbp(fxRates) {
  const rates = fxRates || {};
  return (amount, currency) => {
    const n = Number(amount);
    if (!Number.isFinite(n)) return null;
    const c = (currency || 'GBP').toUpperCase();
    if (c === 'GBP') return n;
    const r = Number(rates[`${c}_GBP`]);
    return Number.isFinite(r) && r > 0 ? n * r : null;
  };
}

/** GBP figure shown in the chosen display currency. */
export function makeDisplay(fxRates, displayCurrency) {
  const eur = Number((fxRates || {}).EUR_GBP);
  return (gbp) => {
    if (gbp === null || gbp === undefined) return null;
    if (displayCurrency === 'EUR') return Number.isFinite(eur) && eur > 0 ? gbp / eur : null;
    return gbp;
  };
}

/** entity_id -> { name, region: 'uk'|'fr'|null, currency } from the group summary. */
export function entityDirectory(groupSummary) {
  const dir = {};
  (groupSummary?.jurisdictions || []).forEach((j) => {
    const region = j.jurisdiction === 'GB' ? 'uk' : j.jurisdiction === 'FR' ? 'fr' : null;
    (j.entities || []).forEach((e) => {
      dir[e.entity_id] = { name: e.name || e.entity_id, region, currency: e.currency || 'GBP' };
    });
  });
  return dir;
}

/** Region of a row: the entity's jurisdiction, else its currency (EUR = France). */
export function regionOf(dir, entityId, currency) {
  const d = dir[entityId];
  if (d && d.region) return d.region;
  return (currency || '').toUpperCase() === 'EUR' ? 'fr' : 'uk';
}

/** Private individuals pay income tax, not corporation tax (Fan, 2026-10-28):
 * { entity_id: 'income_tax' } for every entity on the personal basis. */
export function profitTaxKinds(groupSummary) {
  const out = {};
  (groupSummary?.jurisdictions || []).forEach((j) => (j.entities || []).forEach((e) => {
    if (e.tax_basis === 'personal_income_tax') out[e.entity_id] = 'income_tax';
  }));
  return out;
}

/** Native reserves per entity: { entity_id: { CCY: { vat, corp } } }. */
export function reservesByEntity(groupSummary) {
  const out = {};
  (groupSummary?.jurisdictions || []).forEach((j) => {
    (j.entities || []).forEach((e) => {
      const rows = Array.isArray(e.by_country) && e.by_country.length
        ? e.by_country.map((c) => ({ currency: c.currency, vat: c.vat_reserve, corp: c.corp_tax_reserve }))
        : [{ currency: e.currency, vat: e.vat_reserve, corp: e.corp_tax_reserve }];
      rows.forEach((r) => {
        const vat = Number(r.vat) || 0;
        const corp = Number(r.corp) || 0;
        if (!vat && !corp) return;
        const ccy = r.currency || e.currency || 'GBP';
        out[e.entity_id] = out[e.entity_id] || {};
        const slot = out[e.entity_id][ccy] || { vat: 0, corp: 0 };
        slot.vat += vat;
        slot.corp += corp;
        out[e.entity_id][ccy] = slot;
      });
    });
  });
  return out;
}

function isOpen(o) {
  return !OPEN_STATES_EXCLUDED.has(o.state) && !OPEN_STATES_EXCLUDED.has(o.status);
}

/**
 * Everything the page shows, for one region filter ('all' | 'uk' | 'fr'),
 * optionally narrowed to the entity ids an `entityFilter(id)` accepts.
 * All money in GBP (convert for display with makeDisplay).
 */
export function buildTaxOverview({ groupSummary, countryMonths, debts, taxPaid, vatReturns, vaults, fxRates, region = 'all', year, entityFilter = null, entityNames = null }) {
  // Report rates first; /fx/rates fills currencies the report never used (DKK vaults).
  const fx = { ...(fxRates || {}), ...(taxPaid?.fx_rates_used || {}) };
  const toGbp = makeToGbp(fx);
  const dir = entityDirectory(groupSummary);
  Object.entries(entityNames || {}).forEach(([id, name]) => {
    if (!dir[id]) dir[id] = { name, region: null, currency: 'GBP' };
  });
  // entityFilter (Entities / Personal scope, Fan 2026-10-29): only the
  // companies or people of that scope; null = everyone (Overall).
  const inScope = (entityId) => !entityFilter || entityFilter(entityId);
  const inRegion = (entityId, ccy) => inScope(entityId) && (region === 'all' || regionOf(dir, entityId, ccy) === region);
  let unconverted = 0;
  const conv = (amount, ccy) => {
    if (!Number(amount)) return 0;
    const v = toGbp(amount, ccy);
    if (v === null) { unconverted += 1; return 0; }
    return v;
  };

  const split = { uk: { reserved: 0, owed: 0, overdue: 0, paid: 0, refunds: 0 }, fr: { reserved: 0, owed: 0, overdue: 0, paid: 0, refunds: 0 } };
  const companies = {};
  const company = (id, ccy) => {
    if (!companies[id]) {
      companies[id] = {
        id,
        name: dir[id]?.name || id,
        region: regionOf(dir, id, ccy),
        reserved: 0,
        reservedVat: 0,
        reservedCorp: 0,
        owed: 0,
        overdue: 0,
        paid: 0,
        refunds: 0,
        nextDue: null,
        debts: [],
        paidLines: [],
        kinds: {},
      };
    }
    return companies[id];
  };
  const kindSlot = (c, k) => {
    if (!c.kinds[k]) c.kinds[k] = { key: k, reserved: 0, owed: 0, overdue: 0, paid: 0, refunds: 0, months: blankMonths(), debts: [], paidLines: [], undatedDue: 0 };
    return c.kinds[k];
  };
  const reserveMonths = MONTH_LABELS.map((label) => ({ label, vat: 0, corporation_tax: 0, income_tax: 0 }));
  const nowIdx = String(new Date().getFullYear()) === String(year) ? new Date().getMonth() : 11;
  const addReserve = (c, kind, gbp, i) => {
    if (!gbp) return;
    const idx = i >= 0 && i < 12 ? i : nowIdx;
    const ks = kindSlot(c, kind);
    ks.reserved += gbp;
    ks.months[idx].reserved += gbp;
    reserveMonths[idx][kind] += gbp;
    if (kind === 'vat') c.reservedVat += gbp; else c.reservedCorp += gbp;
    c.reserved += gbp;
    split[c.region].reserved += gbp;
  };
  const monthDetail = {};
  (groupSummary?.jurisdictions || []).forEach((j) => (j.entities || []).forEach((e) => {
    if (Array.isArray(e.month_detail)) monthDetail[e.entity_id] = e.month_detail;
  }));
  // Spread an entity's reserve over the months that earned it (that month's
  // own estimate, positive months only), so the monthly bars add up to the
  // "Reserved now" figure.
  const spread = (id, field, total) => {
    const rows = (monthDetail[id] || []).filter((m) => m.is_actual !== false);
    const w = MONTH_LABELS.map(() => 0);
    rows.forEach((m) => {
      const i = Number(m.month) - 1;
      const v = Number(m[field]);
      if (i >= 0 && i < 12 && v > 0) w[i] += v;
    });
    const sumW = w.reduce((a, b) => a + b, 0);
    if (!sumW) return [[nowIdx, total]];
    return w.map((x, i) => [i, (total * x) / sumW]).filter(([, v]) => v);
  };
  const brosRows = (countryMonths?.months || []).filter((m) => String(m.month || '').slice(0, 4) === String(year));

  // Reserves
  const reserves = reservesByEntity(groupSummary);
  const profitTaxKind = profitTaxKinds(groupSummary);
  const useBros = brosRows.length > 0;
  if (useBros && inRegion('br_ltd', 'GBP')) {
    // BR Ltd: the BR OS ledger month by month (same figures as BR OS > Tax Reserves).
    brosRows.forEach((m) => {
      const c = company('br_ltd', 'GBP');
      const i = Number(String(m.month).slice(5, 7)) - 1;
      addReserve(c, 'vat', conv(m.vat_payable, m.currency), i);
      addReserve(c, 'corporation_tax', conv(m.tax, m.currency), i);
    });
  }
  Object.entries(reserves).forEach(([id, byCcy]) => {
    if (id === 'br_ltd' && useBros) return;
    Object.entries(byCcy).forEach(([ccy, r]) => {
      if (!inRegion(id, ccy)) return;
      const c = company(id, ccy);
      const vat = conv(r.vat, ccy);
      const corp = conv(r.corp, ccy);
      spread(id, 'estimated_vat_reserve', vat).forEach(([i, v]) => addReserve(c, 'vat', v, i));
      spread(id, 'estimated_corp_tax_reserve', corp).forEach(([i, v]) => addReserve(c, profitTaxKind[id] || 'corporation_tax', v, i));
    });
  });

  // Debts
  const today = new Date().toISOString().slice(0, 10);
  (debts?.obligations || []).forEach((o) => {
    if (!isOpen(o) || !inRegion(o.entity_id, o.currency)) return;
    const c = company(o.entity_id, o.currency);
    const v = conv(o.amount, o.currency);
    c.owed += v;
    split[c.region].owed += v;
    if (o.state === 'overdue') { c.overdue += v; split[c.region].overdue += v; }
    if (o.due_date && o.due_date >= today && (!c.nextDue || o.due_date < c.nextDue)) c.nextDue = o.due_date;
    c.debts.push(o);
    const ks = kindSlot(c, debtKind(o));
    ks.owed += v;
    if (o.state === 'overdue') ks.overdue += v;
    ks.debts.push(o);
    const di = o.due_date && String(o.due_date).slice(0, 4) === String(year) ? Number(String(o.due_date).slice(5, 7)) - 1 : -1;
    if (di >= 0 && di < 12) ks.months[di].due += v; else ks.undatedDue += v;
  });

  // Paid and refunds, per month and kind
  const months = MONTH_LABELS.map((label, i) => {
    const row = { month: `${year}-${String(i + 1).padStart(2, '0')}`, label, refunds: 0, total: 0 };
    TAX_KINDS.forEach((k) => { row[k.key] = 0; });
    return row;
  });
  const monthIdx = (d) => {
    const m = Number(String(d || '').slice(5, 7));
    return String(d || '').slice(0, 4) === String(year) && m >= 1 && m <= 12 ? m - 1 : -1;
  };
  (taxPaid?.lines || []).forEach((ln) => {
    if (!inRegion(ln.entity_id, ln.currency)) return;
    const v = ln.paid_base !== undefined && ln.paid_base !== null ? Number(ln.paid_base) || 0 : Math.abs(conv(ln.amount, ln.currency));
    const c = company(ln.entity_id, ln.currency);
    c.paid += v;
    c.paidLines.push(ln);
    split[c.region].paid += v;
    const i = monthIdx(ln.date);
    const ks = kindSlot(c, kindKey(ln.kind));
    ks.paid += v;
    ks.paidLines.push(ln);
    if (i >= 0) ks.months[i].paid += v;
    if (i >= 0) {
      months[i][kindKey(ln.kind)] += v;
      months[i].total += v;
    }
  });
  (taxPaid?.refunds || []).forEach((ln) => {
    if (!inRegion(ln.entity_id, ln.currency)) return;
    const v = Math.abs(conv(ln.amount, ln.currency));
    const c = company(ln.entity_id, ln.currency);
    c.refunds += v;
    c.paidLines.push(ln);
    split[c.region].refunds += v;
    const i = monthIdx(ln.date);
    const ks = kindSlot(c, kindKey(ln.kind));
    ks.refunds += v;
    ks.paidLines.push(ln);
    if (i >= 0) ks.months[i].refunds += v;
    if (i >= 0) {
      months[i].refunds -= v;
      months[i].total -= v;
    }
  });

  // VAT returns (refunds HMRC set off against the debt are not cash)
  const returns = (vatReturns?.returns || []).filter((r) => inRegion(r.entity_id, r.currency));
  // Tax returns tab (Fan, 2026-10-28: "the UK ones don't include the payment
  // plans", "the 36k outstanding VAT is missing", "the PAYE is missing"):
  // returns + every debt, plan instalment and PAYE month + any bank payment
  // or refund no return explains, so the tab adds up to By entity.
  const nameOf = (id) => dir[id]?.name || companies[id]?.name || id;
  const taxItems = mergeTaxItems({
    returns,
    obligations: (debts?.obligations || []).filter((o) => o.status !== 'cancelled' && o.state !== 'cancelled'
      && inRegion(o.entity_id, o.currency)),
    payments: (taxPaid?.lines || []).filter((ln) => inRegion(ln.entity_id, ln.currency)),
    refunds: (taxPaid?.refunds || []).filter((ln) => inRegion(ln.entity_id, ln.currency)),
    nameOf,
    kindLabel: (k) => (TAX_KINDS.find((t) => t.key === k) || {}).label,
    debtKindOf: debtKind,
    kindOf: kindKey,
  });
  const paymentPlans = buildPaymentPlans({
    obligations: (debts?.obligations || []).filter((o) => inRegion(o.entity_id, o.currency)),
    payments: (taxPaid?.lines || []).filter((ln) => inRegion(ln.entity_id, ln.currency)),
    nameOf,
  });
  let offsetRefunds = 0;
  returns.forEach((r) => {
    if (r.result === 'refund' && r.settlement === 'offset') offsetRefunds += Math.abs(conv(r.amount, r.currency));
  });

  // Money actually in the tax vaults (Fan, 2026-10-28): month-end balances,
  // current month live. A month with no snapshot stays null (no bar), never 0.
  const vaultMonths = MONTH_LABELS.map((label) => ({ label, vat: null, corporation_tax: null }));
  const vaultNow = { vat: 0, corporation_tax: 0, total: 0, accounts: 0 };
  const vaultAccounts = (vaults?.accounts || []).filter((a) => inRegion(a.entity_id, a.currency));
  vaultAccounts.forEach((a) => {
    const v = conv(a.balance_now, a.currency);
    vaultNow[a.kind] += v;
    vaultNow.total += v;
    vaultNow.accounts += 1;
  });
  (vaults?.months || []).forEach((m) => {
    if (String(m.month).slice(0, 4) !== String(year)) return;
    const i = Number(String(m.month).slice(5, 7)) - 1;
    if (i < 0 || i > 11) return;
    (m.balances || []).forEach((b) => {
      if (!inRegion(b.entity_id, b.currency)) return;
      const row = vaultMonths[i];
      row[b.kind] = (row[b.kind] || 0) + conv(b.balance, b.currency);
    });
  });

  // Reserve check (Fan, 2026-10-29: "they were put in there but there is no
  // checking whether they were taken out"). Per company and tax:
  //   should be = calculated reserve this year minus that tax already paid
  //               this year (never below 0);
  //   in vault  = live balance of that company's VAT / corp. tax vaults;
  //   gap       = in vault minus should be (below 0 = money missing).
  vaultAccounts.forEach((a) => {
    const v = conv(a.balance_now, a.currency);
    const c = company(a.entity_id, a.currency);
    const ks = kindSlot(c, a.kind);
    ks.vault = (ks.vault || 0) + v;
  });
  const RESERVE_KINDS = new Set(['vat', 'corporation_tax', 'income_tax']);
  Object.values(companies).forEach((c) => {
    c.shouldBe = 0;
    c.vault = 0;
    Object.values(c.kinds).forEach((ks) => {
      if (!RESERVE_KINDS.has(ks.key)) return;
      ks.vault = ks.vault || 0;
      ks.shouldBe = Math.max(0, ks.reserved - ks.paid + ks.refunds);
      ks.gap = ks.vault - ks.shouldBe;
      c.shouldBe += ks.shouldBe;
      c.vault += ks.vault;
    });
    c.gap = c.vault - c.shouldBe;
  });
  const vaultMoves = vaultAccounts.map((a) => ({
    ...a,
    balanceGbp: conv(a.balance_now, a.currency),
    putInGbp: a.put_in == null ? null : conv(a.put_in, a.currency),
    takenOutGbp: a.taken_out == null ? null : conv(a.taken_out, a.currency),
  }));

  const list = Object.values(companies)
    .filter((c) => c.reserved || c.owed || c.paid || c.refunds || c.vault)
    .sort((a, b) => (b.owed + b.reserved + b.paid) - (a.owed + a.reserved + a.paid));

  const sum = (f) => list.reduce((s, c) => s + c[f], 0);
  const nextDue = list.reduce((m, c) => (c.nextDue && (!m || c.nextDue < m) ? c.nextDue : m), null);
  const totalsRow = { label: 'Total', refunds: 0, total: 0 };
  TAX_KINDS.forEach((k) => { totalsRow[k.key] = 0; });
  months.forEach((m) => {
    TAX_KINDS.forEach((k) => { totalsRow[k.key] += m[k.key]; });
    totalsRow.refunds += m.refunds;
    totalsRow.total += m.total;
  });
  const usedKinds = TAX_KINDS.filter((k) => Math.abs(totalsRow[k.key]) > 0.005);

  // Tax debt chart: one bar per company, split by tax.
  const debtRows = list.filter((c) => c.owed > 0.005).sort((a, b) => b.owed - a.owed).map((c) => {
    const row = { label: c.name };
    Object.values(c.kinds).forEach((ks) => { if (ks.owed) row[ks.key] = (row[ks.key] || 0) + ks.owed; });
    return row;
  });
  const debtKinds = TAX_KINDS.filter((k) => debtRows.some((r) => Math.abs(r[k.key] || 0) > 0.005));
  list.forEach((c) => {
    c.kindList = TAX_KINDS.map((k) => c.kinds[k.key]).filter(Boolean)
      .map((ks) => ({ ...ks, label: TAX_KINDS.find((k) => k.key === ks.key).label }));
  });

  return {
    kpis: {
      reserved: sum('reserved'),
      reservedVat: sum('reservedVat'),
      reservedCorp: sum('reservedCorp'),
      shouldBe: sum('shouldBe'),
      vault: sum('vault'),
      gap: sum('gap'),
      owed: sum('owed'),
      overdue: sum('overdue'),
      paid: sum('paid'),
      refunds: sum('refunds'),
      offsetRefunds,
      netPaid: sum('paid') - sum('refunds'),
      nextDue,
      companies: list.length,
    },
    split,
    months,
    totalsRow,
    usedKinds: usedKinds.length ? usedKinds : TAX_KINDS.slice(0, 1),
    companies: list,
    vatReturns: returns,
    taxItems,
    paymentPlans,
    reserveMonths,
    vaultMonths,
    vaultNow,
    vaultAccounts,
    vaultMoves,
    debtChart: { rows: debtRows, kinds: debtKinds },
    unconverted,
    fx,
  };
}

/** /fx/rates ({ base: 'GBP', rates: { EUR: 1.19 } }) -> { EUR_GBP: 0.84 }. */
export function fxFromRates(resp) {
  const out = {};
  const rates = resp?.rates || {};
  if ((resp?.base || 'GBP') !== 'GBP') return out;
  Object.entries(rates).forEach(([c, r]) => {
    const n = Number(r);
    if (c !== 'GBP' && Number.isFinite(n) && n > 0) out[`${c}_GBP`] = 1 / n;
  });
  return out;
}
